import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import { getPatientDocumentFileUrl, includesImage } from 'components/clinical-data/documentUtils';
import TextArea from 'components/final-form/TextArea';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import LoaderComponent from 'components/LoaderComponent';
import PatientMedicalDocumentPdfModal from 'components/pdf-modal/PatientMedicalDocumentPdfModal';
import moment from 'moment';
import React, { useState } from 'react';
import { Field, Form as FinalForm } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, Icon, Modal, Popup, Table } from 'semantic-ui-react';
import { getCdrDocumentFiles, getPatientDocumentText } from 'services/patientRecordServices';
import styled from 'styled-components';
import { PatientDocumentType } from 'ts-types/api.enums';
import {
  CdrDocumentFileContent,
  DoctorDto,
  PatientCdrDocumentDto,
  PatientDocumentTypeDto,
  PatientMedicalDocumentHistoryDto
} from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';
import { getText } from 'utils/textUtils';

const TableInfoContainer = styled.div`
  flex-grow: 1;
  cursor: context-menu;
  overflow: auto;
  margin-top: 15px;
  border: 1px solid rgba(34, 36, 38, .15);

  & .ui.table {
    border: none;
  }

  & .ui.table thead tr:first-child > th {
    position: sticky !important;
    top: 0;
    z-index: 2;
    background: #fff;
  }

  & .ui.table td .description-cell {
    word-break: break-word;
  }

  .date-class {
    display: inline-flex;
    padding-right: 5px;
  }

  .icon-class {
    display: inline-flex;
  }
`;

const StyledTableRow = styled(Table.Row)`
  &.revoked {
    color: rgb(198, 198, 203)
  }

  & .resultStatusIcon {
    margin-left: .5rem
  }
`;

const StyledCell = styled(Table.Cell)`
  & .test-cert-container {
    display: flex;
    max-width: 60px;

    .test-cert-member {
      flex: 50%;

      i.icon {
        cursor: pointer;
      }
    }

    .document-cert-member {
      flex: 50%;

      i.icon {
        cursor: pointer;
      }
    }
  }
`;

const StyledButton = styled(Button)`
  padding: 2px !important;
  margin-bottom: 2px !important;
  background: transparent !important;
`;

const PopupStyle = {
  borderRadius: 5,
  opacity: 0.7,
  padding: '7px'
};

export interface PatientDocumentHistoryViewProps extends MessageSourceProps, AuthConsumerRenderProps {
  patientDocumentTypesMap: { [code: string]: PatientDocumentTypeDto },
  patientDocumentHistory: PatientMedicalDocumentHistoryDto[],
  patientDocument: PatientCdrDocumentDto,
  doctors: DoctorDto[],
  onCloseModal: () => void
}

const PatientDocumentHistoryView = (props: PatientDocumentHistoryViewProps) => {

  const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

  const {patientDocumentHistory, doctors, onCloseModal, getMessage} = props;

  const [patientMedicalDocumentPdfResponse, setPatientMedicalDocumentPdfResponse] =
      useState(undefined as CdrDocumentFileContent[] | undefined);
  const [patientMedicalDocumentPdfModalOpen, setPatientMedicalDocumentPdfModalOpen] = useState(false);
  const [patientMedicalDocumentTextResponse, setPatientMedicalDocumentTextResponse] =
      useState(undefined as string | undefined);
  const [patientMedicalDocumentTextOpen, setPatientMedicalDocumentTextOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(undefined as string | undefined);

  const [requiredDataLoaded, setRequiredDataLoaded] = useState(true);

  const patientDocumentPdfModalOpen = (document: PatientMedicalDocumentHistoryDto) => {

    const documentId = document.id;
    const patientId = document.patientId;

    setRequiredDataLoaded(false);

    getCdrDocumentFiles(patientId, documentId, cancelTokenSource, document.historyItemId)
        .then(response => {
          setPatientMedicalDocumentPdfResponse(response);
          setPatientMedicalDocumentPdfModalOpen(true);
        })
        .catch((e: any) => e.response && handleError(e.response.data))
        .finally(() => setRequiredDataLoaded(true));
  };

  const setPatientDocumentTextModalOpen = (document: PatientMedicalDocumentHistoryDto) => {
    const documentId = document.id;
    const patientId = document.patientId

    setRequiredDataLoaded(false);
    getPatientDocumentText(patientId, documentId, cancelTokenSource, document.historyItemId)
        .then(response => {
          setPatientMedicalDocumentTextOpen(true)
          setPatientMedicalDocumentTextResponse(response);
        })
        .catch((e: any) => handleError(e.response.data))
        .finally(() => setRequiredDataLoaded(true));
  }

  const hidePatientDocumentPdfModal = () => {
    setPatientMedicalDocumentPdfModalOpen(false);
    setPatientMedicalDocumentPdfResponse(undefined);
  };

  const hidePatientDocumentTextModal = () => {
    setPatientMedicalDocumentTextOpen(false);
    setPatientMedicalDocumentTextResponse(undefined);
  };

  const handleError = (error: any) => {

    const errorCode = error.errorCode;
    const knownErrors: Array<string> = [
      errorUtils.unknownDocumentHistory,
      errorUtils.unknownPatientDocumentId,
      errorUtils.unknownPatient
    ];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          setErrorMessage(
              getMessage(violation.details !== "" ? violation.details : `error.${violation.errorCode}`)
          )
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        setErrorMessage(getMessage('error.general'));
      }
    }
  }

  const renderPatientDocumentName = (patientDocument: PatientMedicalDocumentHistoryDto) => {
    const {getMessage} = props;

    return PatientDocumentType.UNABLE_TO_WORK !== patientDocument.type
    && PatientDocumentType.PRESCRIPTION !== patientDocument.type
        ? patientDocument.name
        : getMessage(`patientRecord.patientDocument.${patientDocument.type}`);
  }

  const renderDoctorPatientDocumentCell = (doctorId: number) => {

    let doctorName = "";
    const doctor = doctors.find(doc => doc.id === doctorId);

    if (doctor) {
      const name = doctor.name !== null ? doctor.name : "";
      const firstName = doctor.firstName !== null ? doctor.firstName : "";

      doctorName = name + " " + firstName;
    }

    return doctorName;
  }

  const renderDownloadPatientDocumentCell = (patientDocument: PatientMedicalDocumentHistoryDto) => {
    return (
        <div className="test-cert-container">
          {(patientDocument.documentFile || patientDocument.documentText) &&
              <div className="document-cert-member">
                {patientDocument.documentFile
                    ? <StyledButton
                        icon
                        type="button"
                        onClick={() => patientDocumentPdfModalOpen(patientDocument)}
                    >
                      <Popup
                          trigger={<Icon color="blue" name="print"/>}
                          content={getMessage(`patientRecord.patientDocument.${patientDocument.type}`)}
                          size="small"
                          position="top center"
                          style={PopupStyle}
                          inverted
                      />
                    </StyledButton>
                    : patientDocument.documentText ? renderTextDocumentIcon(patientDocument) : ""
                }
              </div>
          }
          {
              includesImage(patientDocument)
              && patientDocument.documentText
              && renderTextDocumentIcon(patientDocument)
          }
        </div>
    )
  };

  const renderTextDocumentIcon = (patientDocument: PatientMedicalDocumentHistoryDto) => {

    return (
        <StyledButton
            icon
            type="button"
            onClick={() => setPatientDocumentTextModalOpen(patientDocument)}>
          <Popup
              trigger={<Icon color="blue" name="file alternate outline"/>}
              content={getMessage('patientRecord.patientDocument.document')}
              size="small"
              position="top center"
              style={PopupStyle}
              inverted
          />
        </StyledButton>
    );
  }

  const renderPatientMedicalDocumentTextModal = (): React.ReactNode => {

    return (
        <Modal
            open={patientMedicalDocumentTextOpen}
            onClose={() => hidePatientDocumentTextModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>
            {getMessage("patientRecord.patientDocument.textDocument.modalHeader")}
          </Modal.Header>
          <FormModalContentContainer>
            {renderPatientDocumentTextModalContent()}
          </FormModalContentContainer>
        </Modal>
    )
  }

  const renderPatientDocumentTextModalContent = () => {

    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={{textContent: patientMedicalDocumentTextResponse}}
            subscription={{pristine: true, submitting: true}}
            render={renderForm}
        />
    );
  }

  const getDocumentTypeDescription = (documentType: string) => {
    const {patientDocumentTypesMap, language} = props;
    return getText(patientDocumentTypesMap[documentType].description, language);
  }

  const renderForm = () => {

    return (
        <FormContainer>
          <FormContent>
            <PraxFormGrid>
              <Grid.Row>
                <Grid.Column>
                  <label htmlFor="textContent">
                    {getMessage("patientRecord.patientDocument.uploadPatientDocument.comment")}
                  </label>
                  <Field
                      name="textContent"
                      component={TextArea}
                      style={{minHeight: "20vh", maxHeight: "70vh", height: "70vh"}}
                  />
                </Grid.Column>
              </Grid.Row>
            </PraxFormGrid>
            <FormButtonsRow>
              <div style={{float: "right"}}>
                <Button compact type="button" onClick={hidePatientDocumentTextModal}>
                  {getMessage("button.cancel")}
                </Button>
              </div>
            </FormButtonsRow>
          </FormContent>
        </FormContainer>
    );
  };

  return (
      <>
        {
            !requiredDataLoaded
            && <LoaderComponent message={getMessage("loading")}/>
        }
        <FormModalContentContainer>
          {errorMessage &&
              <Grid.Row>
                <Grid.Column width={16}>
                  <StyledErrorMessage onDismiss={() => setErrorMessage(undefined)}>
                    {errorMessage}
                  </StyledErrorMessage>
                </Grid.Column>
              </Grid.Row>
          }
          <TableInfoContainer>
            <Table compact="very">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{getMessage("patientRecord.patientDocumentHistory.name")}</Table.HeaderCell>
                  <Table.HeaderCell>{getMessage("patientRecord.patientDocumentHistory.type")}</Table.HeaderCell>
                  <Table.HeaderCell>
                    {getMessage("patientRecord.patientDocumentHistory.patientDescription")}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {getMessage("patientRecord.patientDocumentHistory.doctor")}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {getMessage("patientRecord.patientDocumentHistory.documentAddedDate")}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {getMessage("patientRecord.patientDocumentHistory.documentChangedDate")}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {getMessage("patientRecord.patientDocumentHistory.documentChangedBy")}
                  </Table.HeaderCell>
                  <Table.HeaderCell>{getMessage("patientRecord.patientDocumentHistory.actionButtons")}</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {
                  patientDocumentHistory.map(patientDocumentHistory => {
                    return (<StyledTableRow key={patientDocumentHistory.historyItemId}>
                      <Table.Cell>{renderPatientDocumentName(patientDocumentHistory)}</Table.Cell>
                      <Table.Cell>{getDocumentTypeDescription(patientDocumentHistory.type)}</Table.Cell>
                      <Table.Cell>
                        <span className="description-cell">{patientDocumentHistory.description}</span>
                      </Table.Cell>
                      <Table.Cell>{renderDoctorPatientDocumentCell(patientDocumentHistory.doctorId)}</Table.Cell>
                      <Table.Cell>
                        {moment(patientDocumentHistory.documentDate).format("DD.MM.YYYY")}
                      </Table.Cell>
                      <Table.Cell>
                        {moment(patientDocumentHistory.changedTimestamp).format("DD.MM.YYYY")}
                      </Table.Cell>
                      <Table.Cell>
                        {patientDocumentHistory.userFirstName + " " + patientDocumentHistory.userLastName}
                      </Table.Cell>
                      <StyledCell>
                        {renderDownloadPatientDocumentCell(patientDocumentHistory)}</StyledCell>
                    </StyledTableRow>)
                  })
                }
              </Table.Body>
            </Table>
          </TableInfoContainer>
          <FormButtonsRow>
            <Button compact
                    type="button"
                    onClick={onCloseModal}
            >
              {getMessage("button.close")}
            </Button>
          </FormButtonsRow>
        </FormModalContentContainer>
        {
            patientMedicalDocumentPdfModalOpen &&
            <PatientMedicalDocumentPdfModal
                pdfResponses={patientMedicalDocumentPdfResponse}
                modalOpen={patientMedicalDocumentPdfModalOpen}
                onCloseModal={hidePatientDocumentPdfModal}
                getPdfUrl={getPatientDocumentFileUrl}
                showDocumentComments={patientMedicalDocumentPdfResponse![0].textContent !== null}
                textContent={patientMedicalDocumentPdfResponse![0].textContent}
            />
        }
        {renderPatientMedicalDocumentTextModal()}
      </>
  );
}

export default withMessages(withAuthContext(PatientDocumentHistoryView));
